// ═══════════════════════════════════════════════════════════
// UI SYSTEM - Menus, HUD, and Overlays
// ═══════════════════════════════════════════════════════════

import { GameState, Bounds } from './types.js';
import { Utils } from './Utils.js';
import { Theme, applyNeonGlow, clearNeonGlow } from './Theme.js';

export class UI {
    private bounds: Bounds;
    private menuPulse: number = 0;
    private highScore: number = 0;
    private isMobile: boolean;
    private isPortrait: boolean;
    private scale: number;
    private hudScoreElement: HTMLElement | null;
    private hudTimeElement: HTMLElement | null;
    private hudOverlay: HTMLElement | null;

    constructor(bounds: Bounds) {
        this.bounds = bounds;
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        this.isPortrait = bounds.height > bounds.width;
        this.scale = this.calculateScale(bounds);
        this.loadHighScore();

        // Get HUD HTML elements
        this.hudScoreElement = document.getElementById('hud-score');
        this.hudTimeElement = document.getElementById('hud-time');
        this.hudOverlay = document.getElementById('hud-overlay');
    }

    private calculateScale(bounds: Bounds): number {
        // Base scale on canvas width relative to standard portrait size (440px)
        const baseWidth = 440;
        const widthScale = bounds.width / baseWidth;

        // Clamp scale between reasonable values
        return Math.max(0.6, Math.min(1.5, widthScale));
    }

    private loadHighScore(): void {
        const saved = localStorage.getItem('cornerBouncer_highScore');
        if (saved) {
            this.highScore = parseInt(saved, 10);
        }
    }

    saveHighScore(score: number): void {
        if (score > this.highScore) {
            this.highScore = score;
            localStorage.setItem('cornerBouncer_highScore', score.toString());
        }
    }

    update(deltaTime: number): void {
        this.menuPulse += deltaTime * Theme.animation.menuPulseSpeed;
    }

    renderMenu(ctx: CanvasRenderingContext2D): void {
        ctx.save();

        // Title (with neon glow)
        const titleSize = Math.floor(70 * this.scale);
        ctx.font = `bold ${titleSize}px "Courier New", monospace`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        // Adjust title position for portrait mode
        const titleY = this.isPortrait ? this.bounds.height / 4 : this.bounds.height / 3;
        const titleSpacing = Math.floor(80 * this.scale);

        // "CORNER" in cyan with neon glow
        applyNeonGlow(ctx, Theme.colors.neonCyan, Theme.glow.uiGlow);
        ctx.fillStyle = Theme.colors.neonCyan;
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 6;
        ctx.strokeText('CORNER', this.bounds.width / 2, titleY);
        ctx.fillText('CORNER', this.bounds.width / 2, titleY);

        // "BOUNCER" in magenta with neon glow
        applyNeonGlow(ctx, Theme.colors.hotMagenta, Theme.glow.uiGlow);
        ctx.fillStyle = Theme.colors.hotMagenta;
        ctx.strokeText('BOUNCER', this.bounds.width / 2, titleY + titleSpacing);
        ctx.fillText('BOUNCER', this.bounds.width / 2, titleY + titleSpacing);
        clearNeonGlow(ctx);

        // Subtitle (yellow neon)
        const subtitleSize = Math.floor(22 * this.scale);
        ctx.font = `${subtitleSize}px "Courier New", monospace`;
        applyNeonGlow(ctx, Theme.colors.electricYellow, 15);
        ctx.fillStyle = Theme.colors.electricYellow;
        ctx.fillText('Turbo-bounce through neon grids', this.bounds.width / 2, titleY + Math.floor(140 * this.scale));
        clearNeonGlow(ctx);

        // Start button with pulse (enhanced neon)
        const pulse = Math.sin(this.menuPulse) * 0.5 + 0.5;
        // Adjust button position for portrait mode
        const buttonY = this.isPortrait
            ? this.bounds.height / 2 + Math.floor(30 * this.scale)
            : this.bounds.height / 2 + Math.floor(90 * this.scale);

        const buttonSize = Math.floor(32 * this.scale);
        ctx.font = `bold ${buttonSize}px "Courier New", monospace`;

        // Pulsing yellow button with intense glow
        const buttonAlpha = 0.7 + pulse * 0.3;
        applyNeonGlow(ctx, Theme.ui.buttonColor, Theme.glow.uiGlow * (1 + pulse));
        ctx.fillStyle = `rgba(255, 255, 0, ${buttonAlpha})`;

        // Mobile-friendly text
        const startText = this.isMobile ? '>> TAP TO RACE <<' : '>> PRESS SPACE TO RACE <<';
        ctx.fillText(startText, this.bounds.width / 2, buttonY);
        clearNeonGlow(ctx);

        // High score
        if (this.highScore > 0) {
            const highScoreSize = Math.floor(24 * this.scale);
            ctx.font = `${highScoreSize}px "Courier New", monospace`;
            ctx.fillStyle = '#ffffff';
            ctx.fillText(`HIGH SCORE: ${Utils.formatScore(this.highScore)}`, this.bounds.width / 2, buttonY + Math.floor(60 * this.scale));
        }

        // Instructions - adjust spacing for portrait
        const instructSize = Math.floor(16 * this.scale);
        ctx.font = `${instructSize}px "Courier New", monospace`;
        ctx.fillStyle = '#888';
        const instructY = this.isPortrait
            ? this.bounds.height - Math.floor(120 * this.scale)
            : this.bounds.height - Math.floor(100 * this.scale);

        ctx.fillText('Hit corners to earn points', this.bounds.width / 2, instructY);
        ctx.fillText('Each corner adds +10 seconds', this.bounds.width / 2, instructY + Math.floor(25 * this.scale));

        const boostText = this.isMobile ? 'TAP for directional boost' : 'SPACE/CLICK for boost';
        ctx.fillText(boostText, this.bounds.width / 2, instructY + Math.floor(50 * this.scale));

        // Retro scanlines effect (less prominent on mobile)
        if (!this.isMobile) {
            this.renderScanlines(ctx);
        }

        ctx.restore();
    }

    /**
     * Update HTML HUD elements (outside canvas)
     */
    updateHUD(score: number, timeRemaining: number): void {
        if (!this.hudScoreElement || !this.hudTimeElement || !this.hudOverlay) return;

        // Show HUD
        this.hudOverlay.style.display = 'flex';

        // Update score
        const scoreText = this.isMobile ? `${Utils.formatScore(score)}` : `SCORE: ${Utils.formatScore(score)}`;
        this.hudScoreElement.textContent = scoreText;

        // Update time
        const timeText = this.isMobile ? `${Utils.formatTime(timeRemaining)}` : `TIME: ${Utils.formatTime(timeRemaining)}`;
        this.hudTimeElement.textContent = timeText;

        // Add low-time class for pulsing effect
        const isLowTime = timeRemaining < 10;
        if (isLowTime) {
            this.hudTimeElement.classList.add('low-time');
        } else {
            this.hudTimeElement.classList.remove('low-time');
        }
    }

    /**
     * Hide HTML HUD elements
     */
    hideHUD(): void {
        if (!this.hudOverlay) return;
        this.hudOverlay.style.display = 'none';
    }

    renderGameOver(ctx: CanvasRenderingContext2D, score: number, isNewHighScore: boolean): void {
        ctx.save();

        // Semi-transparent overlay
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(0, 0, this.bounds.width, this.bounds.height);

        // Game Over text
        const gameOverSize = Math.floor(70 * this.scale);
        ctx.font = `bold ${gameOverSize}px "Courier New", monospace`;
        ctx.fillStyle = '#ff0000';
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 5;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        const centerY = this.bounds.height / 2;

        ctx.strokeText('GAME OVER', this.bounds.width / 2, centerY - Math.floor(100 * this.scale));
        ctx.fillText('GAME OVER', this.bounds.width / 2, centerY - Math.floor(100 * this.scale));

        // Score
        const scoreSize = Math.floor(40 * this.scale);
        ctx.font = `bold ${scoreSize}px "Courier New", monospace`;
        ctx.fillStyle = '#00ffff';
        const scoreLabel = this.isMobile ? 'SCORE: ' : 'FINAL SCORE: ';
        ctx.fillText(`${scoreLabel}${Utils.formatScore(score)}`, this.bounds.width / 2, centerY);

        // High score indicator
        if (isNewHighScore && score > 0) {
            const highScoreSize = Math.floor(30 * this.scale);
            ctx.font = `bold ${highScoreSize}px "Courier New", monospace`;
            ctx.fillStyle = '#ffff00';
            const pulse = Math.sin(Date.now() / 200) * 0.5 + 0.5;
            ctx.globalAlpha = 0.7 + pulse * 0.3;
            ctx.fillText('★ NEW HIGH SCORE! ★', this.bounds.width / 2, centerY + Math.floor(50 * this.scale));
            ctx.globalAlpha = 1;
        } else if (this.highScore > 0) {
            const oldHighScoreSize = Math.floor(24 * this.scale);
            ctx.font = `${oldHighScoreSize}px "Courier New", monospace`;
            ctx.fillStyle = '#888';
            ctx.fillText(`High Score: ${Utils.formatScore(this.highScore)}`, this.bounds.width / 2, centerY + Math.floor(50 * this.scale));
        }

        // Restart prompt
        const restartSize = Math.floor(28 * this.scale);
        ctx.font = `bold ${restartSize}px "Courier New", monospace`;
        ctx.fillStyle = '#ffffff';
        const pulse = Math.sin(this.menuPulse) * 0.5 + 0.5;
        ctx.globalAlpha = 0.5 + pulse * 0.5;

        const restartText = this.isMobile ? 'TAP TO RESTART' : 'PRESS SPACE OR CLICK TO RESTART';
        ctx.fillText(restartText, this.bounds.width / 2, centerY + Math.floor(120 * this.scale));

        ctx.restore();
    }

    private renderScanlines(ctx: CanvasRenderingContext2D): void {
        ctx.save();
        ctx.globalAlpha = 0.05;
        ctx.fillStyle = '#000';

        for (let y = 0; y < this.bounds.height; y += 4) {
            ctx.fillRect(0, y, this.bounds.width, 2);
        }

        ctx.restore();
    }

    updateBounds(bounds: Bounds): void {
        this.bounds = bounds;
        this.isPortrait = bounds.height > bounds.width;
        this.scale = this.calculateScale(bounds);
    }
}
